import { useTranslation } from 'react-i18next';
import { EPDStatusDisplayName } from 'services/EpdClient';
import { DashboardQuickStatsModel } from 'types/dashboard/DashboardListItemModel';
import { HyperLink } from 'styles/Styles.styled';
import { ProcessStatus } from 'constants/constants';
import { EpdLinks } from '../../routes/EpdRoutes';
import AuthService from 'services/AuthService';
import { ExpandablePanel, HeaderCaptionSemiBold } from 'styles/v2/Styles.styled';
import ToggleButton from 'components/v2/compiler/panels/toggleable/ToggleButton';
import { Checkbox } from 'primereact/checkbox';
import { PanelHeaderTemplateOptions } from 'primereact/panel';
import styled from 'styled-components';
import { Option } from 'types/types';

type TProps = {
  quickStats?: DashboardQuickStatsModel;
  companyId: string;
  licenseeSupportEmail: string;
  isUnderLicensee: boolean;
  status: ProcessStatus;
  selectedStatuses: EPDStatusDisplayName[];
  onStatusChange: (statuses: EPDStatusDisplayName[]) => void;
  selectedFormats: Option[];
  onFormatChange: (formats: Option[]) => void;
  showQuickStats?: boolean;
};

type CheckboxVariant = 'gray' | 'yellow' | 'red' | 'green';

const getCheckboxVariant = (statusKey: EPDStatusDisplayName): CheckboxVariant => {
  switch (statusKey) {
    case EPDStatusDisplayName.Reserved:
      return 'gray';
    case EPDStatusDisplayName.Published:
      return 'green';
    case EPDStatusDisplayName.DeclinedVerification:
    case EPDStatusDisplayName.DeclinedPublication:
    case EPDStatusDisplayName.Depublished:
    case EPDStatusDisplayName.Deregistered:
      return 'red';
    default:
      return 'yellow';
  }
};

const allEpdStatuses = Object.values(EPDStatusDisplayName);

const Sidebox = ({
  quickStats,
  companyId,
  licenseeSupportEmail,
  isUnderLicensee,
  status,
  selectedStatuses,
  onStatusChange,
  showQuickStats = true,
}: TProps) => {
  const { t } = useTranslation();

  const toggleStatus = (statusKey: EPDStatusDisplayName) => {
    const isSelected = selectedStatuses.includes(statusKey);
    onStatusChange(isSelected ? selectedStatuses.filter((s) => s !== statusKey) : [...selectedStatuses, statusKey]);
  };

  const createHeaderTemplate = (title: string) => (options: PanelHeaderTemplateOptions) =>
    (
      <div className={`${options.className} ${options.collapsed ? 'header-collapsed' : ''}`}>
        <QuickStatsPanelHeader>
          <HeaderCaptionSemiBold>{title}</HeaderCaptionSemiBold>
          <ToggleButton options={options} />
        </QuickStatsPanelHeader>
      </div>
    );

  const headerTemplate = createHeaderTemplate(t('epdDashboard.quickStats.title'));
  const guidesHeaderTemplate = createHeaderTemplate('Guides and Support');

  const hasAnyQuickStats = quickStats ? Object.values(quickStats.statusCounts).some((count) => count > 0) : false;

  return (
    <>
      {showQuickStats && hasAnyQuickStats && (
        <QuickStatsPanel headerTemplate={headerTemplate} toggleable>
          {allEpdStatuses.map((statusKey) => {
            const count = quickStats?.statusCounts[statusKey] ?? 0;
            if (count === 0) return null;
            const checkboxId = `quick-stats-${statusKey}`;
            return (
              <StatusRow key={statusKey} onClick={() => toggleStatus(statusKey)}>
                <span onClick={(e) => e.stopPropagation()}>
                  <StatusCheckbox
                    inputId={checkboxId}
                    $variant={getCheckboxVariant(statusKey)}
                    checked={selectedStatuses.includes(statusKey)}
                    onChange={() => toggleStatus(statusKey)}
                  />
                </span>
                <StatusLabel>{t(`epdWizard.epdState.status.${statusKey}`)}</StatusLabel>
                <StatusCount>{count}</StatusCount>
              </StatusRow>
            );
          })}
        </QuickStatsPanel>
      )}

      <GuidesAndSupportPanel headerTemplate={guidesHeaderTemplate} toggleable>
        {(AuthService.isCompanyOwner(companyId) || AuthService.isAdmin()) && (
          <GuideSection>
            <GuideSectionTitle>New Invoice Page Available</GuideSectionTitle>
            <GuideSectionText>
              Easily review your invoices and manage billing details in your{' '}
              <HyperLink href={EpdLinks.manageCompany(companyId, 'invoiceList')}>organisation settings</HyperLink>.
            </GuideSectionText>
          </GuideSection>
        )}

        <GuideSection>
          <GuideSectionTitle>Need help getting started?</GuideSectionTitle>
          <GuideSectionText>
            Watch our{' '}
            <HyperLink href="https://www.youtube.com/watch?v=2vJUJFlOxeE&t=5s" target="_blank">
              Portal introduction webinar
            </HyperLink>
            .
          </GuideSectionText>
        </GuideSection>

        <GuideSection>
          <GuideSectionTitle>Understanding EPD development</GuideSectionTitle>
          <GuideSectionText>
            Learn about the{' '}
            <HyperLink href="https://environdec.com/services/epd-creation" target="_blank">
              EPD creation process
            </HyperLink>{' '}
            requirements and key steps from development to publication.
          </GuideSectionText>
        </GuideSection>

        <GuideSection>
          <GuideSectionTitle>EPD Statuses</GuideSectionTitle>
          <GuideSectionText>
            Track your EPD progress by understanding what each status means. Download the{' '}
            <HyperLink href="/EPD_statuses_guide_1.2.pdf" target="_blank">
              statuses guide
            </HyperLink>
            .
          </GuideSectionText>
        </GuideSection>

        <GuideSection>
          <GuideSectionTitle>FAQ</GuideSectionTitle>
          <GuideSectionText>
            Find answers to common questions about EPDs, PCRs, and how to use the platform. Visit our{' '}
            <HyperLink href="https://environdec.freshdesk.com/support/home" target="_blank">
              FAQ
            </HyperLink>
            .
          </GuideSectionText>
        </GuideSection>

        <GuideSection>
          <GuideSectionTitle>Contact</GuideSectionTitle>
          {isUnderLicensee ? (
            <GuideSectionText>
              Your organisation is under a Licensee. For assistance or feedback, please contact{' '}
              {status === ProcessStatus.Fetching ? (
                t('messages.loadingSupportEmail')
              ) : (
                <HyperLink href={`mailto:${licenseeSupportEmail}`}>{licenseeSupportEmail}</HyperLink>
              )}
              . <br /> Alternatively, you can use our{' '}
              <HyperLink href="https://environdec.freshdesk.com/en/support/tickets/new" target="_blank">
                Helpdesk
              </HyperLink>
              .
            </GuideSectionText>
          ) : (
            <GuideSectionText>
              Still need help? Contact us via the{' '}
              <HyperLink href="https://environdec.freshdesk.com/en/support/tickets/new" target="_blank">
                Helpdesk
              </HyperLink>
              .
            </GuideSectionText>
          )}
        </GuideSection>
      </GuidesAndSupportPanel>
    </>
  );
};

const QuickStatsPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const QuickStatsPanel = styled(ExpandablePanel)`
  width: 262px;
  border-radius: 4px;
  box-shadow: 0px 4px 6px 0px rgba(0, 0, 0, 0.11);
  margin-bottom: 20px;
  && .p-panel-header {
    padding: 16px 16px;
    min-height: unset;
    margin-top: 0;
    box-shadow: none;
  }
  && .p-panel-toggler {
    height: 16px;
  }
  && .p-panel-content {
    padding: 0px 16px 12px;
    display: flex;
    flex-direction: column;
  }
`;

const StatusRow = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  &:hover {
    opacity: ${({ $disabled }) => ($disabled ? 1 : 0.8)};
  }
`;

const StatusLabel = styled.label<{ $disabled?: boolean }>`
  flex: 1;
  font-family: Inter;
  font-size: 14px;
  color: ${(props) => props.theme.colors.primaryBlack};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
`;

const StatusCount = styled.span`
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.primaryBlack};
`;

const getVariantColor = (variant: CheckboxVariant, theme: any): string => {
  switch (variant) {
    case 'gray':
      return theme.colors.statusLabelGrayBg;
    case 'yellow':
      return theme.colors.statusLabelYellowBg;
    case 'red':
      return theme.colors.statusLabelRedBg;
    case 'green':
      return theme.colors.statusLabelGreenBg;
  }
};

const StatusCheckbox = styled(Checkbox)<{ $variant: CheckboxVariant }>`
  display: inline-flex;
  align-items: center;
  && .p-checkbox-box {
    border-radius: 2px;
    border-width: 1px;
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    border-color: ${(props) =>
      props.checked ? props.theme.colors.primaryOrange : props.theme.colors.elementsButtonDisabled};
    background-color: ${(props) =>
      props.checked ? props.theme.colors.primaryOrange : getVariantColor(props.$variant, props.theme)};
  }
  && .p-checkbox-box.p-highlight:hover {
    border-color: ${(props) => props.theme.colors.lightOrange};
    background-color: ${(props) => props.theme.colors.lightOrange};
  }
  && .p-checkbox-box .p-checkbox-icon {
    font-size: 10px;
    color: #fff;
  }
`;

const GuidesAndSupportPanel = styled(ExpandablePanel)`
  border-radius: 4px;
  box-shadow: 0px 4px 6px 0px rgba(0, 0, 0, 0.11);
  && .p-panel-header {
    padding: 16px 16px;
    min-height: unset;
    margin-top: 0;
    box-shadow: none;
    background-color: ${(props) => props.theme.colors.elementsDropDown};
  }
  && .p-panel-toggler {
    height: 16px;
  }
  && .p-panel-content {
    padding: 0 16px 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    background-color: ${(props) => props.theme.colors.elementsDropDown};
  }
`;

const GuideSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GuideSectionTitle = styled.p`
  margin: 0;
  font-family: Inter;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: ${(props) => props.theme.colors.primaryBlack};
`;

const GuideSectionText = styled.p`
  margin: 0;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => props.theme.colors.primaryBlack};
  overflow-wrap: break-word;
`;

export default Sidebox;
